/* ─────────────────────────────────────────────────────────────────────
   Tykables — Cart Bundle Conversion
   ─────────────────────────────────────────────────────────────────────
   Detects when a cart contains 4+ eligible single-bag items that
   AREN'T already inside a bundle, and offers the customer a one-click
   conversion into a Case Builder 4-Bag (or 8-Bag) bundle. The eligible-
   row detection is server-side via [data-eligible-bag="1"] (the cart
   Liquid checks variant.metafields.custom.is_single_bag for each line);
   already-bundled rows are excluded via the existing [data-bundle-item]
   attribute, so a customer with an existing bundle and 4+ new bags can
   still get the conversion offer for the new ones.

   Conversion flow:
     1. /cart/update.js with updates: { line_key: 0, ... } removes the
        eligible bag lines (does not require knowing the variant ID for
        removal, only the Shopify-issued line item key).
     2. /cart/add.js with items: [{ id, quantity, properties: { ... }}]
        re-adds them with bundle properties (_box_id, _box_size, Bundle
        label) — same shape Case Builder uses on its own /cart/add.js.
     3. On a 4-bag conversion, a post-conversion upsell modal pops with
        live math (per-bag avg, extra cost, total bundle savings) so the
        customer can elect to upgrade to 8-bag. Accepting redirects to
        Case Builder in upgrade-from mode (?upgrade=1&from=<box_id>).

   Failure handling:
     - Network errors during the round-trip surface a single alert and
       re-enable the banner buttons. We don't try to roll back the
       remove because Shopify's cart API is forgiving — the customer can
       refresh and retry, and any partial removals stay removed (cart
       state remains a valid sub-set of what they had).
   ───────────────────────────────────────────────────────────────────── */

(function () {
  'use strict';

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Per-bag discount amounts — read from data attrs at init time.
  // Defaults match the theme's own defaults so a misconfigured banner
  // (no data attrs) at least produces sane copy.
  var d4PerBag = 12.0;
  var d8PerBag = 14.0;

  function init() {
    var banner = document.querySelector('[data-tyk-bundle-banner]');
    if (!banner) return;

    var d4 = parseFloat(banner.dataset.discount4Amount);
    var d8 = parseFloat(banner.dataset.discount8Amount);
    if (!isNaN(d4) && d4 > 0) d4PerBag = d4;
    if (!isNaN(d8) && d8 > 0) d8PerBag = d8;

    renderBanner();
    setupUpsellModal();
  }

  // Find cart rows that are eligible single-bag variants AND not yet
  // part of an existing bundle. This is recomputed on every render so
  // it stays correct after dynamic mutations (e.g. customer manually
  // removed a bag and the banner needs to re-evaluate).
  function getEligibleRows() {
    var rows = document.querySelectorAll('[data-eligible-bag="1"]');
    var out = [];
    for (var i = 0; i < rows.length; i++) {
      if (!rows[i].hasAttribute('data-bundle-item')) {
        out.push(rows[i]);
      }
    }
    return out;
  }

  function renderBanner() {
    var banner = document.querySelector('[data-tyk-bundle-banner]');
    if (!banner) return;

    var eligible = getEligibleRows();
    if (eligible.length < 4) {
      banner.hidden = true;
      return;
    }

    var copyEl    = banner.querySelector('[data-tyk-bundle-banner-copy]');
    var actionsEl = banner.querySelector('[data-tyk-bundle-banner-actions]');
    if (!copyEl || !actionsEl) return;

    if (eligible.length >= 8) {
      // ≥8 eligible — offer the 8-bag conversion outright.
      var savings8 = 8 * d8PerBag;
      copyEl.innerHTML =
        '<div class="tyk-bundle-convert-banner__title">Bundle &amp; save $' + savings8.toFixed(0) + '</div>' +
        '<div class="tyk-bundle-convert-banner__sub">Convert 8 of your bags into an 8-Bag Case Builder bundle — $' + d8PerBag.toFixed(0) + ' off every bag.</div>';
      actionsEl.innerHTML =
        '<button type="button" class="tbtn tbtn--yellow" data-tyk-bundle-convert="8">Convert to 8-Bag Bundle</button>' +
        '<button type="button" class="tyk-bundle-convert-banner__dismiss" data-tyk-bundle-dismiss>Maybe later</button>';
    } else {
      // 4–7 eligible — offer 4-bag conversion. The post-conversion
      // upsell handles the path to 8-bag (where the customer picks 4
      // more in Case Builder upgrade mode), so we don't need to
      // pre-pitch 8-bag here.
      var savings4 = 4 * d4PerBag;
      var leftover = eligible.length - 4;
      var leftoverNote = leftover > 0
        ? ' (' + leftover + ' other bag' + (leftover === 1 ? '' : 's') + ' will stay individual)'
        : '';
      copyEl.innerHTML =
        '<div class="tyk-bundle-convert-banner__title">Bundle &amp; save $' + savings4.toFixed(0) + '</div>' +
        '<div class="tyk-bundle-convert-banner__sub">Convert 4 of your bags into a 4-Bag Case Builder bundle — $' + d4PerBag.toFixed(0) + ' off every bag' + leftoverNote + '.</div>';
      actionsEl.innerHTML =
        '<button type="button" class="tbtn tbtn--yellow" data-tyk-bundle-convert="4">Convert to 4-Bag Bundle</button>' +
        '<button type="button" class="tyk-bundle-convert-banner__dismiss" data-tyk-bundle-dismiss>Maybe later</button>';
    }

    banner.hidden = false;

    // Wire up the freshly-injected buttons. We re-bind every render
    // since the DOM is rebuilt; cleanup is implicit.
    var convertBtns = banner.querySelectorAll('[data-tyk-bundle-convert]');
    for (var i = 0; i < convertBtns.length; i++) {
      convertBtns[i].addEventListener('click', function (e) {
        var size = parseInt(e.currentTarget.dataset.tykBundleConvert, 10);
        convertToBundle(size);
      });
    }
    var dismissBtn = banner.querySelector('[data-tyk-bundle-dismiss]');
    if (dismissBtn) {
      dismissBtn.addEventListener('click', function () { banner.hidden = true; });
    }
  }

  function convertToBundle(size) {
    var eligible = getEligibleRows();
    var picks = eligible.slice(0, size);
    if (picks.length < size) {
      window.alert('Not enough eligible bags to bundle. Please refresh the page and try again.');
      return;
    }

    var banner = document.querySelector('[data-tyk-bundle-banner]');
    var btns = banner ? banner.querySelectorAll('button') : [];
    for (var i = 0; i < btns.length; i++) {
      btns[i].disabled = true;
    }

    // Generate a fresh box id so this bundle is distinct from any other
    // bundles already in the cart. Same format Case Builder uses.
    var boxId = 'tykbox_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 8);

    var humanList = picks.map(function (row) {
      var nameEl = row.querySelector('.tyk-cart-row__name');
      return nameEl ? nameEl.textContent.trim() : 'Bag';
    }).join(', ');

    // Step 1 payload — remove the picked rows by line item key.
    var updates = {};
    picks.forEach(function (row) {
      updates[row.dataset.cartItemKey] = 0;
    });

    // Step 2 payload — re-add as bundle line items.
    var addItems = picks.map(function (row, idx) {
      return {
        id: parseInt(row.dataset.variantId, 10),
        quantity: 1,
        properties: {
          '_box_id':     boxId,
          '_box_size':   String(size),
          '_box_index':  String(idx + 1),
          '_box_bundle': humanList,
          'Bundle':      size + '-Bag Box'
        }
      };
    });

    fetch('/cart/update.js', {
      method:      'POST',
      credentials: 'same-origin',
      headers:     { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body:        JSON.stringify({ updates: updates })
    })
      .then(function (r) {
        if (!r.ok) throw new Error('remove-failed');
        return r.json();
      })
      .then(function () {
        return fetch('/cart/add.js', {
          method:      'POST',
          credentials: 'same-origin',
          headers:     { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body:        JSON.stringify({ items: addItems })
        });
      })
      .then(function (r) {
        if (!r.ok) throw new Error('add-failed');
        return r.json();
      })
      .then(function () {
        if (size === 4) {
          // Don't reload yet — show the upsell first. The modal's close
          // handler does the reload so the customer sees the new bundle
          // group with YOUR BUNDLE pill regardless of upsell choice.
          showPostConvertUpsell(boxId);
        } else {
          // 8-bag conversion: no further upsell, reload to render the
          // new bundle group.
          window.location.reload();
        }
      })
      .catch(function (err) {
        console.error('Bundle conversion failed:', err);
        window.alert("Couldn't convert your bags to a bundle. Please refresh and try again.");
        for (var j = 0; j < btns.length; j++) { btns[j].disabled = false; }
      });
  }

  // ── Post-conversion upsell ────────────────────────────────────────
  // Mirrors Case Builder's 4→8 upsell math but uses live cart data so
  // bag prices reflect what the customer actually bundled (not a
  // hard-coded $48 retail assumption). Pops AFTER the cart is updated
  // so the modal's "extra cost" calc is based on real /cart.js prices.

  function setupUpsellModal() {
    var modal = document.querySelector('[data-tyk-bundle-convert-upsell]');
    if (!modal) return;

    var closeBtn   = modal.querySelector('[data-tyk-bundle-convert-upsell-close]');
    var declineBtn = modal.querySelector('[data-tyk-bundle-convert-upsell-decline]');
    var acceptBtn  = modal.querySelector('[data-tyk-bundle-convert-upsell-accept]');

    function close() {
      modal.classList.remove('is-open');
      // Reload so the cart reflects the just-converted bundle. Without
      // this, the customer sees stale pre-conversion line items.
      window.location.reload();
    }
    if (closeBtn)   closeBtn.addEventListener('click', close);
    if (declineBtn) declineBtn.addEventListener('click', close);
    modal.addEventListener('click', function (e) {
      if (e.target === modal) close();
    });

    if (acceptBtn) {
      acceptBtn.addEventListener('click', function () {
        var boxId = acceptBtn.dataset.boxId || '';
        // Hand the upgrade context to Case Builder via query string.
        // sessionStorage as a belt-and-suspenders fallback in case the
        // query param gets stripped by some redirect upstream.
        try { sessionStorage.setItem('tykBabUpgradeFromBoxId', boxId); } catch (_) {}
        window.location.href = '/pages/mix-and-match?upgrade=1&from=' + encodeURIComponent(boxId);
      });
    }
  }

  function showPostConvertUpsell(boxId) {
    var modal = document.querySelector('[data-tyk-bundle-convert-upsell]');
    if (!modal) {
      // Modal not on the page — fall back to a plain reload.
      window.location.reload();
      return;
    }

    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var bundleItems = (cart.items || []).filter(function (it) {
          return it.properties && it.properties._box_id === boxId;
        });

        if (bundleItems.length !== 4) {
          // Something's off — bail to a reload rather than show stale
          // numbers in the upsell.
          window.location.reload();
          return;
        }

        // Sum line prices in dollars (Shopify gives line item prices in
        // cents). original_price is the pre-discount unit price; that's
        // what we want for the "what would 8-bag cost" math.
        var sumDollars = bundleItems.reduce(function (acc, it) {
          return acc + ((it.original_price || it.price || 0) / 100);
        }, 0);

        var pay4              = sumDollars - 4 * d4PerBag;
        var pay8              = 2 * sumDollars - 8 * d8PerBag;
        var extraCost         = Math.max(0, pay8 - pay4);
        var extraPerBag       = extraCost / 4;
        var totalAdvantage    = 8 * d8PerBag - 4 * d4PerBag;
        var perBagAdvantage   = d8PerBag - d4PerBag;

        var bodyEl = modal.querySelector('[data-tyk-bundle-convert-upsell-body]');
        if (bodyEl) {
          bodyEl.innerHTML =
            '<p>Pick 4 more bags at just <strong>$' + extraPerBag.toFixed(2) + ' each</strong></p>' +
            '<p>Every bag would save $' + d8PerBag.toFixed(2) + ' (vs. $' + d4PerBag.toFixed(2) + ' on the 4-bag bundle) — $' + perBagAdvantage.toFixed(2) + ' more off per bag.</p>' +
            '<p class="tyk-bab__upsell-modal-savings">+$' + extraCost.toFixed(2) + ' added • $' + totalAdvantage.toFixed(2) + ' more in total bundle savings</p>';
        }

        var acceptBtn = modal.querySelector('[data-tyk-bundle-convert-upsell-accept]');
        if (acceptBtn) acceptBtn.dataset.boxId = boxId;

        modal.classList.add('is-open');
      })
      .catch(function () {
        // Cart fetch failed — best effort: reload.
        window.location.reload();
      });
  }
})();
